import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, map, tap, catchError, of, retry, switchMap, throwError, timer } from 'rxjs';

import { getApiBaseUrl } from '@core/api-url';
import { getPaymentCollectionsAdvancedApiBaseUrl } from '@core/reconciliation-api-url';
import { environment } from '@env/environment';
import { resolveIcon } from '../layout/icon-registry';
import type { NavItem } from '../layout/navigation.config';
import { TenantService } from './tenant.service';

const MANAGEMENT_DASHBOARD_ROUTE = '/dashboard-gerencial';

/** Shape returned by GET /api/me/navigation (icons as plain names). */
interface NavNodeResponse {
  key: string;
  label: string;
  icon: string | null;
  route: string | null;
  type: string;
  children: NavNodeResponse[];
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly http = inject(HttpClient);
  private readonly tenantService = inject(TenantService);
  private readonly url = `${getApiBaseUrl()}/api/me/navigation`;

  private readonly _navigation = signal<NavItem[]>([]);
  private readonly _loaded = signal(false);
  private readonly _loadError = signal(false);

  /** Navigation tree already filtered by tenant features and the user's permissions. */
  readonly navigation = this._navigation.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly loadError = this._loadError.asReadonly();

  /** Fetches the navigation for the active tenant (X-Tenant-Key is added by the interceptor). */
  load(): Observable<NavItem[]> {
    this._loadError.set(false);

    return this.http.get<NavNodeResponse[]>(this.url).pipe(
      retry({
        count: 2,
        delay: (error, retryCount) => this.retryTransientFailure(error, retryCount)
      }),
      map(tree => this.resolveExternalNavigation(
        this.organizeAdministrativeNavigation(
          this.removeDeprecatedFinancialReconciliationNavigation(tree.map(node => this.toNavItem(node)))
        )
      )),
      switchMap(items => this.applyManagementDashboard(items)),
      tap(items => {
        this._navigation.set(items);
        this._loaded.set(true);
        this._loadError.set(false);
      }),
      catchError(() => {
        this._navigation.set([]);
        this._loaded.set(false);
        this._loadError.set(true);
        return of([]);
      })
    );
  }

  clear(): void {
    this._navigation.set([]);
    this._loaded.set(false);
    this._loadError.set(false);
  }

  hasRoute(url: string): boolean {
    const normalizedUrl = this.normalizeRoute(url);
    return this._navigation().some(item => this.itemMatchesRoute(item, normalizedUrl));
  }

  private toNavItem(node: NavNodeResponse): NavItem {
    return {
      key: node.key,
      name: node.label,
      href: node.route ?? '',
      icon: resolveIcon(node.icon),
      description: '',
      children: node.children?.length ? node.children.map(child => this.toNavItem(child)) : undefined
    };
  }

  private resolveExternalNavigation(items: NavItem[]): NavItem[] {
    const baseUrl = this.normalizeExternalBaseUrl(environment.goskalerWebBaseUrl);
    if (!baseUrl) return items;

    return items.map(item => this.resolveExternalNavItem(item, baseUrl));
  }

  private resolveExternalNavItem(item: NavItem, baseUrl: string): NavItem {
    const externalPath = item.key?.startsWith('goskaler.')
      ? this.normalizeExternalPath(item.href)
      : null;

    return {
      ...item,
      externalUrl: externalPath ? this.buildExternalUrl(baseUrl, externalPath) : item.externalUrl,
      children: item.children?.map(child => this.resolveExternalNavItem(child, baseUrl))
    };
  }

  private normalizeExternalBaseUrl(url: string | null | undefined): string | null {
    const value = url?.trim();
    if (!value || value.startsWith('__')) {
      return null;
    }

    return value.replace(/\/+$/, '');
  }

  private buildExternalUrl(baseUrl: string, path: string): string {
    const url = path.startsWith('http://') || path.startsWith('https://')
      ? path
      : `${baseUrl}${path}`;

    return this.withCurrentTenant(url);
  }

  private normalizeExternalPath(path: string | null | undefined): string | null {
    const value = path?.trim();
    if (!value || value.startsWith('http://') || value.startsWith('https://')) {
      return value ?? null;
    }

    return value.startsWith('/') ? value : `/${value}`;
  }

  private withCurrentTenant(url: string): string {
    const tenantKey = this.tenantService.tenantKey();
    if (!tenantKey) return url;

    try {
      const externalUrl = new URL(url);
      externalUrl.searchParams.set('contractId', tenantKey);
      return externalUrl.toString();
    } catch {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}contractId=${encodeURIComponent(tenantKey)}`;
    }
  }

  /**
   * Los contratos de servicio habilitan comercialmente módulos del tenant y no son un maestro
   * operativo. Se conservan para compatibilidad con el backend, pero solo se muestran dentro del
   * Centro de control cuando este fue autorizado para el usuario bootstrap.
   */
  private organizeAdministrativeNavigation(items: NavItem[]): NavItem[] {
    let serviceContracts: NavItem | null = null;
    const removeContracts = (nodes: NavItem[]): NavItem[] => nodes.flatMap(node => {
      if (this.normalizeRoute(node.href) === '/maestros/contratos-servicio') {
        serviceContracts = node;
        return [];
      }
      return [{ ...node, children: node.children ? removeContracts(node.children) : undefined }];
    });
    const result = removeContracts(items);
    if (!serviceContracts) return result;
    return result.map(node => node.key === 'centro-control'
      ? { ...node, children: [...(node.children ?? []), { ...serviceContracts!, href: '/control-center/contracts' }] }
      : node);
  }

  /**
   * The previous financial reconciliation workspace remains routable for compatibility and
   * historical support, but is no longer part of the product navigation. Its configuration
   * screens are hidden alongside it so Multifuente is the only financial reconciliation model
   * presented to regular users.
   */
  private removeDeprecatedFinancialReconciliationNavigation(items: NavItem[]): NavItem[] {
    const hiddenRoutes = new Set([
      '/reconciliation/financial',
      '/reconciliation/legacy/financial',
      '/maestros/politicas-liquidacion'
    ]);

    return items.flatMap(item => {
      if (hiddenRoutes.has(this.normalizeRoute(item.href))) return [];

      return [{
        ...item,
        children: item.children
          ? this.removeDeprecatedFinancialReconciliationNavigation(item.children)
          : undefined
      }];
    });
  }

  /**
   * El tablero gerencial se habilita por contrato con un perfil de reporte (MANAGEMENT_DASHBOARD) en Nexu Advanced, no
   * con un módulo de Nexus. Si el menú lo trae, se pregunta a Advanced y se oculta cuando el contrato no lo tiene.
   */
  private applyManagementDashboard(items: NavItem[]): Observable<NavItem[]> {
    const isDashboard = (item: NavItem) => this.normalizeRoute(item.href).startsWith(MANAGEMENT_DASHBOARD_ROUTE);
    const contains = (nodes: NavItem[]): boolean => nodes.some(node => isDashboard(node) || contains(node.children ?? []));
    if (!contains(items)) return of(items);
    const remove = (nodes: NavItem[]): NavItem[] => nodes.flatMap(node => isDashboard(node)
      ? []
      : [{ ...node, children: node.children ? remove(node.children) : undefined }])
      .filter(node => !node.children || node.children.length > 0 || !!node.href);
    return this.http.get<{ enabled: boolean }>(`${getPaymentCollectionsAdvancedApiBaseUrl()}/api/management-dashboard/status`).pipe(
      map(status => status?.enabled ? items : remove(items)),
      catchError(() => of(remove(items)))
    );
  }

  private itemMatchesRoute(item: NavItem, url: string): boolean {
    const href = this.normalizeRoute(item.href);
    const matches = href === url || (href !== '/' && url.startsWith(`${href}/`));

    return matches || !!item.children?.some(child => this.itemMatchesRoute(child, url));
  }

  private normalizeRoute(route: string): string {
    const path = route.split(/[?#]/)[0] || '/';
    return path.length > 1 ? path.replace(/\/$/, '') : path;
  }

  private retryTransientFailure(error: unknown, retryCount: number): Observable<number> {
    if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
      return throwError(() => error);
    }

    return timer(retryCount * 500);
  }
}
